'use client';

import { useTranslations } from 'next-intl';
import type { JSX } from 'react';

import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Icon } from '@/components/ui/icon';
import { Alert01Icon, ChampionIconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';

const LOADING_ROW_IDS: readonly string[] = ['first', 'second', 'third'];

// The compact hero is 128px on desktop and 168px below 768px; the skeleton row
// keeps the same box so the loading → ready swap is shape-stable.
const COMPACT_HERO_SKELETON_CLASS = 'h-32 @max-[767px]/competition-loading:h-42';

function CompactHeroSkeleton(): JSX.Element {
  return (
    <Panel density="flush" className={`overflow-hidden ${COMPACT_HERO_SKELETON_CLASS}`}>
      <PanelContent className="flex h-full flex-col justify-between p-4">
        <div className="flex items-center justify-between gap-4" aria-hidden="true">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-28 rounded-pill" />
            <Skeleton className="h-6 w-16 rounded-pill" />
            <Skeleton className="h-6 w-14 rounded-pill" />
          </div>
          <Skeleton className="h-8 w-28 rounded-pill" />
        </div>
        <div className="flex items-end justify-between gap-6" aria-hidden="true">
          <div className="grid gap-2">
            <Skeleton className="h-7 w-64 max-w-full" />
            <Skeleton className="h-4 w-44" />
          </div>
          <Skeleton className="h-8 w-28" />
        </div>
      </PanelContent>
    </Panel>
  );
}

export function CompetitionsListLoading(): JSX.Element {
  const t = useTranslations('competitions.list');

  return (
    <output
      data-testid="competitions-list-loading"
      aria-busy="true"
      className="flex w-full min-w-0 flex-col gap-3 @container/competition-loading"
    >
      <span className="sr-only">{t('loading')}</span>
      {LOADING_ROW_IDS.map((id) => (
        <CompactHeroSkeleton key={id} />
      ))}
    </output>
  );
}

/** Route-level skeleton for `/social/competitions` — phase pills and rows. */
export function CompetitionsPageLoading(): JSX.Element {
  return (
    <div className="flex w-full min-w-0 flex-col gap-4 md:gap-6" aria-busy="true">
      <div className="flex gap-2 overflow-hidden">
        <Skeleton className="h-9 w-16 shrink-0 rounded-pill" />
        <Skeleton className="h-9 w-20 shrink-0 rounded-pill" />
        <Skeleton className="h-9 w-24 shrink-0 rounded-pill" />
        <Skeleton className="h-9 w-24 shrink-0 rounded-pill" />
      </div>
      <CompetitionsListLoading />
    </div>
  );
}

export function CompetitionsListError({ onRetry }: { onRetry: () => void }): JSX.Element {
  const t = useTranslations('competitions.list');

  return (
    <Panel data-testid="competitions-list-error" density="compact">
      <PanelContent className="flex flex-col gap-4 py-1 sm:flex-row sm:items-center">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-feedback-error-surface">
          <Icon icon={Alert01Icon} className="size-icon-lg text-feedback-error-content" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-heading-xs text-foreground-primary">{t('errorTitle')}</h2>
          <p className="mt-1 text-body text-foreground-secondary">{t('errorDescription')}</p>
        </div>
        <AnimatedButton variant="secondary" onClick={onRetry}>
          {t('retry')}
        </AnimatedButton>
      </PanelContent>
    </Panel>
  );
}

export function CompetitionsListEmpty({
  kind,
  phaseLabel,
  onShowAll,
}: {
  kind: 'empty' | 'no-matches';
  phaseLabel: string;
  onShowAll: () => void;
}): JSX.Element {
  const t = useTranslations('competitions.list');
  const noMatches = kind === 'no-matches';

  return (
    <EmptyState
      data-testid={`competitions-list-${kind}`}
      icon={ChampionIconSolid}
      framed
      title={noMatches ? t('noMatchesTitle', { phase: phaseLabel }) : t('emptyTitle')}
      description={noMatches ? t('noMatchesDescription') : t('emptyDescription')}
      action={
        noMatches ? (
          <AnimatedButton variant="secondary" onClick={onShowAll}>
            {t('showAll')}
          </AnimatedButton>
        ) : undefined
      }
    />
  );
}
